'use client';

import { useEffect, useSyncExternalStore } from 'react';

/**
 * Rendered height of the ContentHub header, for the offsets that have to clear it.
 *
 * The header is not always 64px: when stacked (BCOM via ContentHubHeader's brand list, or
 * the CMS `is-stacked-mobile-header-enabled` attribute; MPH-1163) it renders two rows below
 * 992px — full logo on top, collapsed CSW below —
 * so any offset hardcoded to HEADER_HEIGHT slides under it. ContentHubHeader measures the
 * real height into a CSS variable (for styles) and a store (for IntersectionObserver
 * rootMargins); both fall back to the given height before the first measure, in SSR and on
 * pages without the ContentHub header (external-header brands, the app).
 */
const HEADER_HEIGHT_CSS_VAR = '--tsx-header-height';
const NAVIGATION_SELECTOR = '[data-contenthub-id="navigation"]';
// Set by the ContentHub nav while its stacked layout is active (STACKED_ROOT_MOBILE_CLASS in the library).
const STACKED_CLASS = 'hub-nav-stacked-root-mobile';

let measuredHeight: number | undefined;
const listeners = new Set<() => void>();

const setMeasuredHeight = (height: number | undefined) => {
  if (height === measuredHeight) return;
  measuredHeight = height;
  if (height === undefined) document.documentElement.style.removeProperty(HEADER_HEIGHT_CSS_VAR);
  else document.documentElement.style.setProperty(HEADER_HEIGHT_CSS_VAR, `${height}px`);
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** CSS value for an offset that clears the header, e.g. `top: ${headerHeightCss(64)};`. */
export const headerHeightCss = (fallbackPx: number): string => `var(${HEADER_HEIGHT_CSS_VAR}, ${fallbackPx}px)`;

/** Numeric header height for JS offsets (IntersectionObserver rootMargins). */
export const useHeaderHeight = (fallbackPx: number): number =>
  useSyncExternalStore(
    subscribe,
    () => measuredHeight ?? fallbackPx,
    () => fallbackPx
  );

/**
 * Keeps the header height up to date while the ContentHub header is mounted. Opening the
 * search turns the stacked layout off (the header drops to one row under the search overlay),
 * so the height is held while it is open — otherwise the page would jump by a row behind the
 * overlay on every open and close.
 *
 * The nav's own stacked class is the signal, not the CSW's `csw:isExpanded` event: the class
 * goes in the same render that drops the second row, whereas the event comes from a CSW
 * effect after the header has already resized. Once the header has been stacked, a missing
 * class means the search is open; the class coming back re-measures even when the height does
 * not change (closing at desktop width, where the header is one row either way).
 */
export const useMeasureHeaderHeight = () => {
  useEffect(() => {
    const navigation = document.querySelector<HTMLElement>(NAVIGATION_SELECTOR);
    if (!navigation) return undefined;

    let hasBeenStacked = false;
    const measure = () => {
      const isStacked = navigation.classList.contains(STACKED_CLASS);
      hasBeenStacked ||= isStacked;
      if (hasBeenStacked && !isStacked) return;
      // 0 while hidden (the app's injected CSS): keep the fallback rather than offset by nothing.
      if (navigation.offsetHeight > 0) setMeasuredHeight(navigation.offsetHeight);
    };

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(navigation);
    const classObserver = new MutationObserver(measure);
    classObserver.observe(navigation, { attributes: true, attributeFilter: ['class'] });
    measure();

    return () => {
      resizeObserver.disconnect();
      classObserver.disconnect();
      setMeasuredHeight(undefined);
    };
  }, []);
};
